import camelCaseKeys from 'camelcase-keys';
import cleanDeep from 'clean-deep';
import snakeCaseKeys from 'snakecase-keys';

import { createTransformationStream } from './createTransformationStream';

/**
 *
 * Cognite AI API Client
 * https://api-docs.cogheim.net/redoc/#tag/Chat-completions
 *
 * Keep it as function so that we can pass it to OpenAI client
 * and re-use it internally
 */
export async function cogniteAIApiFetch(url: string | URL | Request, init?: RequestInit) {
  let convertedRequestBody;
  let isStreamingRequest = false;

  /**
   * NOTE(eiriklv): The request from the openai-node library will contain snake_case properties,
   * but since the cog-ai endpoint expects camelCase we have to convert
   * the request before calling the cog-ai endpoint
   */
  if (init?.body) {
    const parsedBodyInSnakeCase = JSON.parse(init.body.toString());
    const parsedBodyInCamelCase = camelCaseKeys(parsedBodyInSnakeCase);

    /**
     * NOTE(eiriklv): Detect if we're expected to stream the response
     */
    isStreamingRequest = !!parsedBodyInCamelCase.stream;

    /**
     * NOTE(eiriklv): openai-node is passing some params we don't support in cog-ai,
     * like `parsed` and `parsed_parameters` - where all have the value `null`
     */
    const parsedBodyInCamelCaseWithoutNulls = cleanDeep(parsedBodyInCamelCase, {
      emptyArrays: false,
      emptyObjects: false,
      emptyStrings: false,
      NaNValues: false,
      nullValues: true,
      undefinedValues: true,
    });

    convertedRequestBody = JSON.stringify(parsedBodyInCamelCaseWithoutNulls);
  }

  let overriddenRequestHeaders = {} as HeadersInit;
  let token = '';

  /**
   * NOTE(eiriklv): We have to override some of the request headers
   * that openai-node supplied to conform to what the cog-ai endpoint expects
   * (things related to auth, etc)
   */
  if (init?.headers) {
    for (const [key, value] of init.headers.entries()) {
      if (key.toLowerCase() === 'authorization') {
        token = value;
      }
      // Skip all OpenAI headers
      if (!key.toLowerCase().startsWith('x-stainles') && !key.toLowerCase().startsWith('user-agent')) {
        overriddenRequestHeaders[key as keyof HeadersInit] = value;
      }
    }
    overriddenRequestHeaders = {
      ...overriddenRequestHeaders,
      authorization: token,
      accept: isStreamingRequest ? 'text/event-stream' : 'application/json',
      'content-type': 'application/json',
    };
  }

  const convertedRequest = {
    ...(init || {}),
    headers: overriddenRequestHeaders,
    body: convertedRequestBody,
  };

  /**
   * NOTE(eiriklv): The response from our cog-ai endpoint will contain camelCase properties,
   * but since the openai-node library and typings expect snake_case we have to convert
   * the response before passing it on to the library for handling
   */
  const response = await fetch(url.toString(), convertedRequest);

  /**
   * NOTE(eiriklv): Handle streaming requests,
   * as they also need to be transformed from camelCase
   * to snake_case
   */
  if (isStreamingRequest) {
    // Make sure that you call createTransformationStream() here
    // to transform the response body
    // otherwise it will keep the old reference
    // and the previous stream will be locked since reader will be in use
    return new Response(response.body?.pipeThrough(createTransformationStream()), response);
  }

  /**
   * Handle the regular response format (non-streaming)
   */
  const responseDataInCamelCase = await response.json();
  const responseDataInSnakeCase = snakeCaseKeys(responseDataInCamelCase);

  /**
   * NOTE(eiriklv): Recreate the Response,
   * now with snake_case instead of camelCase
   */
  const convertedResponse = new Response(JSON.stringify(responseDataInSnakeCase), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });

  return convertedResponse;
}
